import { useQuery } from "@tanstack/react-query";

// MUI
import { Collapse, Divider, Grid, Skeleton, Stack } from "@mui/material";

// API
import { getOEMData } from "@DataPages/api/oem";

// Helpers
import { getOEMParameters } from "@/Common/helpers/OEMs";

// Components
import OemDataPages from "@DataPages/components/OEM/Tables/OemDataTable/OemDataPages";
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

// Assets
import empty from "@/assets/images/commons/empty.png";

// Styles
import "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable.css";

// Types
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { IOEM } from "@Common/interfaces/location";
import SectionContainer from "@Layouts/components/SectionContainer";
import { TransitionGroup } from "react-transition-group";

type FinancialDataTabProps = Readonly<{
	locationId: string;
	selected: ICommissionCycle;
	locationOEMs: IOEM[];
}>;

// Constants
const FINANCIAL_STATEMENTS = "Financial Statements";

export default function FinancialDataTab(props: FinancialDataTabProps) {
	const { data: oems, isLoading } = useQuery({
		queryKey: ["oemData", props.locationId, props.selected._id, FINANCIAL_STATEMENTS],
		queryFn: async () =>
			getOEMData({
				programName: FINANCIAL_STATEMENTS,
				locationId: props.locationId,
				commissionCycleId: props.selected._id,
			}),
		enabled: Boolean(props.selected._id) && Boolean(props.locationId),
		staleTime: Infinity,
	});

	if (isLoading) {
		return (
			<TransitionGroup key="transitionKey" style={{ minHeight: "500px" }}>
				<Collapse key={1}>
					<SectionContainer title={"Loading"} collapsible loading={true} sx={{ boxShadow: "none !important" }}>
						<Grid item mt={0} xs={12} mb={2} sx={{ padding: "0px 30px" }}>
							<Skeleton variant="rectangular" width={200} height={20} sx={{ borderRadius: "6px", marginTop: "20px" }} />
							<TableLoader content rows={7} />
						</Grid>
					</SectionContainer>
				</Collapse>
			</TransitionGroup>
		);
	}

	if (!oems?.pages?.length) {
		return (
			<EmptyBody
				image={empty}
				title={"No FS data found"}
				imageStyles={{ width: "330px", marginTop: "65px" }}
				bodyHeight={"320px"}
				sx={{ height: "500px !important", boxShadow: "none !important" }}
			/>
		);
	}

	return (
		<Stack sx={{ minHeight: "500px !important", width: "100%" }}>
			{oems?.pages.map((oem, key) => {
				const oemParams = getOEMParameters({
					oemReference: oem?.oemReference,
					dealerCodeFS: oem?.dealerCodeFS,
					locationOEMs: props?.locationOEMs,
				}) as { oemDisplayName: string };

				const { oemDisplayName } = oemParams || {};
				return (
					<Stack className="oem-data-tab-collapse" key={oem.oemReference} style={{ width: "100%" }}>
						<CollapseArea
							title={oemDisplayName || oem.oemName}
							sxTitle={{ paddingLeft: 5, color: `#2357BE ${oems?.pages.length === 1 ? "!important" : ""}` }}
							collapsible={oems?.pages.length > 1}
							openCollapse={oems?.pages.length === 1}
						>
							<OemDataPages
								locationId={props.locationId}
								selected={props.selected}
								oemDataTab={FINANCIAL_STATEMENTS}
								isPaginationPage
								oem={oem.oemReference}
								dealerCodeFS={oem.dealerCodeFS}
								currentPageNumbers={oem.pageNames}
								defaultPage={oem.page}
							/>
						</CollapseArea>
						{Boolean(key !== oems?.pages?.length - 1) && (
							<Divider className="!border-disabled" sx={{ borderWidth: "1px", marginX: 5 }} variant="middle" />
						)}
					</Stack>
				);
			})}
		</Stack>
	);
}
